import * as React from 'react';
import classnames from 'classnames';
import { deburr, kebabCase } from 'lodash';

import PieChart from 'components/graphs/echarts/pie-chart/PieChart';
import BarChartSimple from 'components/graphs/bar-chart-simple/BarChartSimple';

import { __t, applyTranslations } from 'utils/language';
import { getReplacementValue } from 'modules/insights/sov/utils/sov';
import { getCountriesBarChartData } from 'utils/charts/bar-chart';
import { colors } from 'utils/charts/utils';
import { convertRatio } from 'utils/helper/common';
import {
  displayPieChart,
  getAgePieChartData,
  getGenderPieChartData,
  preparePieChartData
} from 'utils/charts/pie-chart';

import './sov-audience-cards.scss';

import type { PieChart as PieChartType, SOVAudienceByPropName } from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));

type Props = {
  results: Array<SOVAudienceByPropName>,
  filter: string
};

const COMMON_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY = 'sov_shared';

class SOVAudienceCards extends React.PureComponent<Props> {
  renderBarChartCountries(item: SOVAudienceByPropName): React.ReactNode {
    const countries = getCountriesBarChartData(item.data, 'percentage', false);

    const updatedCountries = countries.map(country => ({
      ...country,
      value: convertRatio(country.value)
    }));

    const titles = [
      __t(`${COMMON_MSG_KEY}.basics.country`), '',
      __t(`${COMMON_MSG_KEY}.basics.followers`),
      __t(`${COMMON_MSG_KEY}.basics.share`)
    ];

    return (
      <BarChartSimple
        items={updatedCountries}
        titles={titles}
        color={colors.audience[1]}
        showPercentMarker
      />
    );
  }

  renderPieChart(item: SOVAudienceByPropName, keyValue: string): React.ReactNode {
    const { filter } = this.props;
    let data: Array<PieChartType> = [];

    if (filter === 'gender') {
      data = getGenderPieChartData(item.data);
    }

    if (filter === 'age') {
      data = getAgePieChartData(item.data);
    }

    const pieChartData = preparePieChartData(data, 'audience');

    return (
      <div className="sov-audience-card__pie-chart">
        <PieChart
          id={`sov-audience-card__pie-chart-${keyValue}-${filter}`}
          options={pieChartData}
          hide={!displayPieChart(data)}
        />
      </div>
    );
  }

  renderCard = (item: SOVAudienceByPropName, index: number): JSX.Element => {
    const { results, filter } = this.props;
    const keyValue = `${kebabCase(deburr(item.query))}-${index}`;

    const cardClasses = classnames('sov-audience-card', {
      'sov-audience-card--with-divider': index % 2 === 0,
      'sov-audience-card--no-styles': results.length <= 1
    });

    return (
      <div
        key={`sov-audience-card-${keyValue}`}
        className={cardClasses}
      >
        <div className="sov-audience-card__query">
          <div className="sov-audience-card__query-label">
            {__t(`${SHARED_MSG_KEY}.labels.keyword`)}:
          </div>
          <div className="sov-audience-card__query-value">
            {getReplacementValue(item.query)}
          </div>
        </div>
        <div className="sov-audience-card__statistics">
          {
            filter === 'country' ?
              this.renderBarChartCountries(item) :
              this.renderPieChart(item, keyValue)
          }
        </div>
      </div>
    );
  };

  render(): React.ReactNode {
    const { results } = this.props;

    return (
      <div className="sov-audience-cards">
        {
          results.map(this.renderCard)
        }
      </div>
    );
  }
}

export default SOVAudienceCards;
